import cx from 'clsx';
import { FormattedMessage, useIntl } from 'react-intl';
import { Controller, useForm } from 'react-hook-form';
import { Button, Disclaimer, TextField, useToast } from '@repo/design-system-kit';
import { VideoAnimations } from '@repo/shared-kit/components';
import { useNavigate } from '@repo/poly-hooks/routing';
import { useEmberService } from '@qonto/react-migration-toolkit/react/hooks';
import { useDebounceCallback } from 'usehooks-ts';
import { HTTP_STATUS_CODES } from 'register-partners-js/constants/http-status-codes';
import type User from 'register-partners-js/models/user';
import { isNetworkManagerError } from 'register-partners-js/services/network-manager';
import { BackButton } from '../../../components/back-button/back-button';
import { RegistrationLayout } from '../../../components/layouts/registration';
import { EmailConfirmationSubtitle } from './email-confirmation-subtitle';
import { useEmailConfirmationMutation } from './use-email-confirmation-mutation';
import { useResendEmailMutation } from './use-resend-email-mutation';
import styles from './email-confirmation-route.module.css';

const VIDEO_PATH = 'https://qonto.com/register/videos';

interface FormValues {
  code: string;
}

interface Redirection {
  id: string;
  companyCreationFromScratch?: boolean;
  legalFlow?: string;
  partnerType?: string;
  partnerName?: string;
}

interface EmailConfirmationRouteProps {
  user: User;
  redirection: Redirection;
}

export function EmailConfirmationRoute({ user, redirection }: EmailConfirmationRouteProps) {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const store = useEmberService('store');
  const { showToast } = useToast();
  const isQontoInvoicingFlow = redirection.legalFlow === 'qonto_invoicing';

  const {
    control,
    formState: { isSubmitting },
    handleSubmit,
    setError,
    clearErrors,
  } = useForm<FormValues>({
    defaultValues: {
      code: user.code || '',
    },
    mode: 'onSubmit',
  });

  const emailConfirmationMutation = useEmailConfirmationMutation({
    redirectionId: redirection.id,
    isQontoInvoicingFlow,
  });

  const { mutate: resendEmail, isPending: isResendPending } = useResendEmailMutation({
    user,
    onSuccess: () => {
      showToast({
        type: 'success',
        text: formatMessage({ id: 'toasts.email_confirm_code_sent' }),
      });
    },
  });

  const debouncedResendEmail = useDebounceCallback(resendEmail, 500);

  // Sync code to Ember Data model - required for the Ember session authenticator bridge.
  // Can be removed once session is fully migrated to React.
  const syncCodeToEmberModel = (code: string) => {
    // Guard against writes after the Ember record has been unloaded (on success),
    // which would crash with "cache.getAttr on null" in @ember-data.
    // oxlint-disable-next-line typescript/consistent-type-assertions -- Ember runtime properties not in TS type
    const emberUser = user as unknown as { isDestroyed: boolean; isDestroying: boolean };
    if (emberUser.isDestroyed || emberUser.isDestroying) {
      return;
    }
    user.code = code;
  };

  const onSubmit = async (data: FormValues) => {
    syncCodeToEmberModel(data.code);

    try {
      const result = await emailConfirmationMutation.mutateAsync({
        code: data.code,
        email: user.email ?? '',
      });

      // SCA flow was cancelled by the user — do not unload the record.
      if (result.status === 'cancelled') {
        return;
      }

      // oxlint-disable-next-line typescript/consistent-type-assertions -- bridging untyped Ember store during migration
      (store as unknown as { unloadRecord: (record: unknown) => void }).unloadRecord(user);
    } catch (error) {
      if (isNetworkManagerError(error)) {
        if (error.status === HTTP_STATUS_CODES.UNAUTHORIZED) {
          setError('code', {
            message: formatMessage({ id: 'confirmation-code.errors.invalid_code' }),
          });
        } else if (error.status === HTTP_STATUS_CODES.TOO_MANY_REQUESTS) {
          setError('code', {
            message: formatMessage({ id: 'confirmation-code.errors.flood_email_code' }),
          });
        }
      }
    }
  };

  return (
    <RegistrationLayout
      aside={
        isQontoInvoicingFlow ? undefined : (
          <VideoAnimations
            backgroundColor="#f5f8b4"
            fallback="/illustrations/animations/fallbacks/register-email-confirm-fallback.png"
            src={`${VIDEO_PATH}/register-confirm-email-intro.mp4`}
          />
        )
      }
    >
      <form
        className={styles.form}
        data-test-email-confirmation-form
        onSubmit={handleSubmit(onSubmit)}
      >
        <h1 className="heading-section">
          <FormattedMessage id="confirmation-code.title" />
        </h1>
        <p className={cx('body-standard', styles.subtitle)} data-hj-suppress>
          <EmailConfirmationSubtitle email={user.email ?? ''} redirection={redirection} />
        </p>

        <Controller
          control={control}
          name="code"
          render={({ field: { ref: _, onChange, value }, fieldState: { error, invalid } }) => (
            <TextField
              className={styles.codeField}
              errorMessage={invalid ? error?.message : undefined}
              inputMode="numeric"
              isDisabled={isSubmitting}
              isInvalid={invalid}
              label={formatMessage({ id: 'confirmation-code.label' })}
              onChange={newValue => {
                onChange(newValue);
                clearErrors('code');
                syncCodeToEmberModel(newValue);
              }}
              placeholder="XXX XXX"
              type="number"
              value={value}
            />
          )}
        />

        <Disclaimer.Inline className={cx('body-standard', styles.disclaimerSpacing)}>
          <span>
            <FormattedMessage id="confirmation-code.disclaimer.text" />
            <Button
              isDisabled={isResendPending}
              onPress={() => {
                debouncedResendEmail();
              }}
              variant="tertiary"
            >
              <FormattedMessage id="confirmation-code.disclaimer.link" />
            </Button>
          </span>
        </Disclaimer.Inline>

        <Button
          className={cx(styles.submitBtn, isQontoInvoicingFlow && styles.fullWidth)}
          data-test-email-confirmation-submit
          isPending={emailConfirmationMutation.isPending}
          type="submit"
        >
          <FormattedMessage id="confirmation-code.cta" />
        </Button>

        {isQontoInvoicingFlow ? (
          <div className={styles.backButton}>
            <BackButton
              data-test-back-button-simple
              onPress={() => {
                navigate(`/${redirection.id}`);
              }}
            />
          </div>
        ) : null}
      </form>
    </RegistrationLayout>
  );
}
